import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import {
  Container,
  Card,
  CardBody,
  Table,
  Badge,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import moment from "moment";
import "moment/locale/es";
import { toast } from "react-toastify";
import { isEqual } from "lodash";
import { formatCurrency } from "../../utils/formatCurrency";
import { auth } from "../../firebase";

// URL del servidor TCSocket (ajustable por env var).
const TCSOCKET_URL =
  process.env.REACT_APP_TCSOCKET_URL || "https://api.ticketcontrol.net";

// Persistencia de drafts en localStorage — se limpia al enviar con éxito.
const DRAFT_STORAGE_KEY = (machineKey) => `tariff_draft_${machineKey}`;

// ── Tipos de timelapse + metadata de presentación ──
// `labelV2` opcional: nombre a partir de la versión 2.0 de tc_qml
// (en v2.0 renombramos "Mensual" → "Abonados"). Resolver con typeLabelFor().
const TYPE_ORDER = [
  { key: "hour", label: "Por hora", icon: "schedule", color: "#556ee6" },
  { key: "tn", label: "Por turno", icon: "nights_stay", color: "#f1b44c" },
  { key: "stay", label: "Estadía", icon: "event", color: "#34c38f" },
  { key: "plana", label: "Tarifa plana", icon: "horizontal_rule", color: "#a569bd" },
  {
    key: "cc",
    label: "Mensual (Cta.Cte.)",
    labelV2: "Abonados",
    icon: "calendar_month",
    color: "#50a5f1",
  },
];

// True si la versión es >= 2.0 (primer dígito del semver).
function isV2OrAbove(versionDB) {
  if (!versionDB || typeof versionDB !== "string") return false;
  const major = parseInt(versionDB.split(".")[0], 10);
  return !isNaN(major) && major >= 2;
}

// Resuelve el label del tipo según la versión del cliente.
function typeLabelFor(item, versionDB) {
  if (item.labelV2 && isV2OrAbove(versionDB)) return item.labelV2;
  return item.label;
}

const DAY_LABELS = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];

// ─────────────────────────────────────────────────────────
// Draft helpers (modo edición)
// ─────────────────────────────────────────────────────────
function deepClone(x) {
  return x == null ? x : JSON.parse(JSON.stringify(x));
}

// Normaliza los valores del snapshot que la app publica en un formato que ella
// misma no aceptaría de vuelta. Sin esto, un valor heredado que el usuario ni
// tocó viaja en el payload y hace fallar el guardado ENTERO (incluidos los
// precios), porque la validación de la máquina es todo-o-nada.
//
// ccDebtWarnMode: la app persiste "warn" para "sólo notificar", pero su
// validador remoto solo acepta {"only_warn","warn_block"} (mismatch histórico).
// Por el cable únicamente pueden viajar esos dos literales.
function normalizeConfig(config) {
  const out = deepClone(config || {});
  if ("ccDebtWarnMode" in out) {
    // Semántica real: solo "warn_block" bloquea el ingreso; cualquier otro
    // valor ("warn", null, "") significa sólo notificar.
    out.ccDebtWarnMode =
      out.ccDebtWarnMode === "warn_block" ? "warn_block" : "only_warn";
  }
  return out;
}

// Construye un draft inicial a partir del snapshot de Firestore.
function buildInitialDraft(tariffs) {
  if (!tariffs) return null;
  return {
    schemaVersion: tariffs.schemaVersion || 1,
    currencySymbol: tariffs.currencySymbol || "$",
    categories: deepClone(tariffs.categories || []),
    timelapses: deepClone(tariffs.timelapses || []),
    rates: deepClone(tariffs.rates || []),
    config: normalizeConfig(tariffs.config),
  };
}

// Devuelve SOLO las claves de config que el usuario cambió respecto del
// snapshot. La máquina valida y aplica únicamente las claves presentes, así
// que no mandar lo que no se tocó evita que un valor heredado inválido —de
// esta clave o de cualquier otra— bloquee el guardado completo.
//
// `timeBaseHour` viaja siempre: la regla de negocio TIME_BASE_INVALID de la
// máquina lo lee con default 0, y omitirlo saltearía esa validación cruzada.
function buildConfigDiff(draftConfig, baseConfig) {
  const draftCfg = draftConfig || {};
  const baseCfg = baseConfig || {};
  const out = {};
  for (const [key, value] of Object.entries(draftCfg)) {
    if (!isEqual(value, baseCfg[key])) out[key] = value;
  }
  if (draftCfg.timeBaseHour !== undefined) {
    out.timeBaseHour = draftCfg.timeBaseHour;
  }
  return out;
}

// ─────────────────────────────────────────────────────────
// Mensajes amigables de error (los códigos técnicos van a consola).
// ─────────────────────────────────────────────────────────

// Título amigable para el toast según el código de error del servidor.
function friendlyErrorTitle(code, httpStatus) {
  switch (code) {
    case "PAYLOAD_INVALID":
      return "Hay datos con formato inválido. Revisá los campos marcados.";
    case "BUSINESS_RULE_FAILED":
      return "No se pueden aplicar los cambios. Revisá los detalles abajo.";
    case "APPLY_FAILED":
      return "La máquina no pudo guardar los cambios. Intentá de nuevo.";
    case "MACHINE_OFFLINE":
      return "La máquina no está conectada. Verificá que TicketControl esté abierto y con internet.";
    case "MACHINE_DISCONNECTED":
      return "La máquina se desconectó mientras se aplicaban los cambios.";
    case "TIMEOUT":
      return "La máquina no respondió a tiempo. Probá nuevamente.";
    case "NO_PERMISSION":
      return "No tenés permiso para editar el tarifario.";
    case "AUTH_FAILED":
      return "Tu sesión expiró. Iniciá sesión nuevamente.";
    case "UNEXPECTED":
      return "Ocurrió un error inesperado en la máquina.";
    default:
      if (httpStatus === 0 || !httpStatus) return "No se pudo conectar al servidor.";
      return "No se pudieron aplicar los cambios.";
  }
}

// Convierte un "field" técnico (p. ej. "categories(id=6)" o "rates[3].value")
// en un texto amigable usando los datos del draft actual para resolver nombres.
function humanizeField(field, draft) {
  if (!field || typeof field !== "string") return null;

  // categories(id=X) / timelapses(id=X)
  let m = field.match(/^categories\(id=(-?\d+)\)/);
  if (m) {
    const id = parseInt(m[1], 10);
    const cat = draft?.categories?.find((c) => c.id === id);
    return cat ? `Categoría "${cat.name}"` : `Categoría`;
  }
  m = field.match(/^timelapses\(id=(-?\d+)\)/);
  if (m) {
    const id = parseInt(m[1], 10);
    const tl = draft?.timelapses?.find((t) => t.id === id);
    return tl ? `Período "${tl.label}"` : `Período`;
  }

  // categories[N].prop / timelapses[N].prop / rates[N].prop
  const catPropNames = { name: "nombre", order: "orden", hiddenBy: "visibilidad" };
  const tlPropNames = {
    label: "nombre",
    time: "duración",
    tolerance: "tolerancia",
    type: "tipo",
    weekDay: "días",
    timeInit: "hora de inicio",
    timeEnd: "hora de fin",
    onlyTime: "tarifa de arranque",
    isBase: "tarifa base",
  };
  m = field.match(/^categories\[(\d+)\]\.(\w+)/);
  if (m) {
    const cat = draft?.categories?.[parseInt(m[1], 10)];
    const p = catPropNames[m[2]] || m[2];
    return cat ? `Categoría "${cat.name}" (${p})` : `Categoría (${p})`;
  }
  m = field.match(/^timelapses\[(\d+)\]\.(\w+)/);
  if (m) {
    const tl = draft?.timelapses?.[parseInt(m[1], 10)];
    const p = tlPropNames[m[2]] || m[2];
    return tl ? `Período "${tl.label}" (${p})` : `Período (${p})`;
  }
  m = field.match(/^rates\[(\d+)\]\.(\w+)/);
  if (m) {
    const rate = draft?.rates?.[parseInt(m[1], 10)];
    if (rate) {
      const cat = draft?.categories?.find((c) => c.id === rate.categoryId);
      const tl = draft?.timelapses?.find((t) => t.id === rate.timelapseId);
      if (cat && tl) return `Precio de "${cat.name}" en "${tl.label}"`;
    }
    return `Precio`;
  }

  // config.key
  const configLabels = {
    timeBaseHour: "Fracción de hora",
    initTolerance: "Tolerancia inicial",
    hourPrepaidAllowed: "Cobro anticipado por hora",
    stayPrepaidAllowed: "Cobro anticipado de estadía",
    tnPrepaidAllowed: "Cobro anticipado de turno",
    planaPrepaidAllowed: "Cobro anticipado de tarifa plana",
    hourReservation: "Reserva por hora",
    stayReservation: "Reserva de estadía",
    tnReservation: "Reserva de turno",
    planaReservation: "Reserva de tarifa plana",
    planaApplyOnce: "Tarifa plana una sola vez",
    stayManualCut: "Corte manual de estadía",
    stayCut: "Horario de finalización de estadía",
    stayEditAllowed: "Horario de estadía editable",
    stayOverflowLikeHour: "Excedente de estadía por hora",
    tnOverflowLikeHour: "Excedente de turno por hora",
    presenceStay: "Presencia manual en estadías",
    presenceCc: "Presencia manual en abonados",
    ccBillingPeriod: "Período de cobro",
    ccSurcharge: "Recargo por pago fuera de término",
    ccSurchargeDays: "Días para aplicar recargo",
    ccSurchargePercentage: "Porcentaje de recargo",
    ccDiscount: "Descuento por pago en término",
    ccDiscountDays: "Días para aplicar descuento",
    ccDiscountPercentage: "Porcentaje de descuento",
    ccVerifyPlate: "Verificar placa al abrir ticket",
    ccDebtWarnEnabled: "Notificación de deuda",
    ccDebtWarnDays: "Día de notificación de deuda",
    ccDebtWarnMode: "Modo de notificación de deuda",
  };
  m = field.match(/^config\.(\w+)/);
  if (m) {
    const label = configLabels[m[1]] || m[1];
    return `Configuración: ${label}`;
  }

  // Fallback — devolver como estaba
  return field;
}

// ─────────────────────────────────────────────────────────
// Validación client-side del draft
// Retorna un objeto { [fieldId]: message } con los errores detectados.
// fieldIds: "category.<id>.<prop>" | "timelapse.<id>.<prop>"
// Esta validación complementa la del backend (tariffs_validator.py):
// acá damos feedback en vivo ANTES de enviar, evitando el viaje al
// servidor para errores básicos.
// ─────────────────────────────────────────────────────────
function validateDraftClient(draft) {
  const errors = {};
  if (!draft || typeof draft !== "object") return errors;

  // Categorías — nombre obligatorio
  (draft.categories || []).forEach((c) => {
    if (!c || typeof c !== "object") return;
    if (!c.name || !String(c.name).trim()) {
      errors[`category.${c.id}.name`] = "Requerido";
    }
  });

  // Timelapses
  (draft.timelapses || []).forEach((t) => {
    if (!t || typeof t !== "object") return;
    const tid = t.id;

    // Nombre obligatorio
    if (!t.label || !String(t.label).trim()) {
      errors[`timelapse.${tid}.label`] = "Requerido";
    }

    // Duración según tipo
    const tp = t.type;
    if (tp === "hour" || tp === "tn" || tp === "stay") {
      if (!t.time || t.time <= 0) {
        errors[`timelapse.${tid}.time`] = "La duración debe ser mayor a 0";
      }
    }
    if (tp === "plana") {
      if (!t.timeInit) {
        errors[`timelapse.${tid}.timeInit`] = "Hora de inicio requerida";
      }
      if (!t.timeEnd) {
        errors[`timelapse.${tid}.timeEnd`] = "Hora de fin requerida";
      }
    }

    // Vigencia (solo hour): si está en modo restringido (hay horario o días),
    // tiene que haber al menos 1 día seleccionado.
    if (tp === "hour") {
      const hasTime = !!t.timeInit || !!t.timeEnd;
      const hasDays = Array.isArray(t.weekDay) && t.weekDay.length > 0;
      if (hasTime && !hasDays) {
        errors[`timelapse.${tid}.weekDay`] = "Elegí al menos un día";
      }
    }
  });

  return errors;
}

// Compara dos payloads ignorando updatedAt y otros volátiles.
// Usamos isEqual (lodash) — comparación estructural profunda, inmune al
// orden de keys que Firestore puede reordenar al serializar/deserializar.
function payloadsEqual(a, b) {
  if (!a || !b) return a === b;
  const pick = (p) => ({
    schemaVersion: p.schemaVersion,
    categories: p.categories,
    timelapses: p.timelapses,
    rates: p.rates,
    config: p.config,
  });
  return isEqual(pick(a), pick(b));
}

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────
function formatDuration(minutes) {
  if (!minutes || minutes <= 0) return "";
  if (minutes >= 60 && minutes % 60 === 0) {
    const h = minutes / 60;
    return h === 1 ? "1 h" : `${h} h`;
  }
  if (minutes > 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h} h ${m} min`;
  }
  return `${minutes} min`;
}

function formatWeekDay(weekDay) {
  if (!Array.isArray(weekDay) || weekDay.length === 0) return "";
  const days = weekDay
    .map((d) => parseInt(d, 10))
    .filter((d) => !isNaN(d) && d >= 0 && d <= 6);
  if (days.length === 0) return "";
  if (days.length === 7) return "Todos los días";
  return days.map((d) => DAY_LABELS[d]).join("/");
}

// Badge de check/cruz para flags booleanos
function Flag({ active, label, tooltip }) {
  return (
    <div
      className="d-flex align-items-center"
      title={tooltip || ""}
      style={{ fontSize: 13, minWidth: 180 }}
    >
      <span
        className="material-symbols-rounded me-2"
        style={{
          fontSize: 18,
          color: active ? "#34c38f" : "#ced4da",
          flexShrink: 0,
        }}
      >
        {active ? "check_circle" : "radio_button_unchecked"}
      </span>
      <span style={{ color: active ? "#333" : "#999" }}>{label}</span>
    </div>
  );
}

// Fila de "dato simple" (label + value) para la pestaña Tarifas
function Info({ label, value, hint }) {
  return (
    <div style={{ fontSize: 13, minWidth: 180 }}>
      <div>
        <span className="text-muted me-2">{label}:</span>
        <b>{value}</b>
      </div>
      {hint && (
        <div className="small text-muted" style={{ fontSize: 11, marginTop: 2 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Tab: Categorías
// ─────────────────────────────────────────────────────────
// Helper de mensaje de error inline debajo de un input.
function FieldError({ message }) {
  if (!message) return null;
  return (
    <div
      style={{
        color: "#f46a6a",
        fontSize: 11,
        marginTop: 2,
        lineHeight: 1.2,
      }}
    >
      {message}
    </div>
  );
}

// Input inline para el nombre de categoría (modo edición).
function CategoryNameInput({ value, onChange, error }) {
  const [local, setLocal] = useState(value || "");
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setLocal(value || "");
  }, [value, focused]);
  return (
    <div style={{ display: "inline-block" }}>
      <Input
        type="text"
        bsSize="sm"
        value={local}
        invalid={!!error}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          const trimmed = (local || "").trim();
          if (trimmed && trimmed !== value) onChange(trimmed);
          else setLocal(value || "");
        }}
        onChange={(e) => setLocal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        style={{ maxWidth: 250 }}
      />
      <FieldError message={error} />
    </div>
  );
}

function CategoriesTab({ categories, canEdit, updateDraft, genNegId, versionDB, errors = {} }) {
  const hasCategories = categories && categories.length > 0;

  const handleAdd = () => {
    const newId = genNegId();
    updateDraft((d) => {
      const nextOrder = d.categories.reduce((m, c) => Math.max(m, c.order || 0), 0) + 1;
      d.categories.push({
        id: newId,
        name: "Nueva categoría",
        order: nextOrder,
        hiddenBy: [],
      });
    });
  };

  const handleDelete = (catId) => {
    updateDraft((d) => {
      d.categories = d.categories.filter((c) => c.id !== catId);
      // También limpiar rates asociadas (el aplicador la recrea en 0 según necesidad)
      d.rates = d.rates.filter((r) => r.categoryId !== catId);
    });
  };

  const handleRename = (catId, newName) => {
    updateDraft((d) => {
      const c = d.categories.find((x) => x.id === catId);
      if (c) c.name = newName;
    });
  };

  const handleToggleHidden = (catId, typeKey, shouldHide) => {
    updateDraft((d) => {
      const c = d.categories.find((x) => x.id === catId);
      if (!c) return;
      const hb = Array.isArray(c.hiddenBy) ? [...c.hiddenBy] : [];
      if (shouldHide && !hb.includes(typeKey)) hb.push(typeKey);
      if (!shouldHide) {
        const idx = hb.indexOf(typeKey);
        if (idx >= 0) hb.splice(idx, 1);
      }
      c.hiddenBy = hb;
    });
  };

  if (!hasCategories && !canEdit) {
    return (
      <Card>
        <CardBody className="text-muted">Sin categorías configuradas.</CardBody>
      </Card>
    );
  }

  const sorted = [...(categories || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );
  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center mb-3">
          <span
            className="material-symbols-rounded me-2"
            style={{ fontSize: 22, color: "#556ee6" }}
          >
            directions_car
          </span>
          <h5 className="mb-0">Categorías de vehículo</h5>
          <Badge color="light" className="ms-2 text-muted">
            {sorted.length}
          </Badge>
          {canEdit && (
            <Button
              color="primary"
              outline
              size="sm"
              className="ms-auto"
              onClick={handleAdd}
            >
              <span
                className="material-symbols-rounded align-middle me-1"
                style={{ fontSize: 14 }}
              >
                add
              </span>
              Agregar categoría
            </Button>
          )}
        </div>
        <Table className="mb-0 align-middle" bordered hover>
          <thead className="table-light">
            <tr>
              <th style={{ width: 60 }}>#</th>
              <th style={{ minWidth: 200 }}>Nombre</th>
              <th>Oculta en</th>
              {canEdit && <th style={{ width: 60 }}></th>}
            </tr>
          </thead>
          <tbody>
            {sorted.map((c, idx) => {
              const hidden = Array.isArray(c.hiddenBy) ? c.hiddenBy : [];
              return (
                <tr key={c.id}>
                  <td className="text-muted">{idx + 1}</td>
                  <td>
                    {canEdit ? (
                      <CategoryNameInput
                        value={c.name}
                        onChange={(v) => handleRename(c.id, v)}
                        error={errors[`category.${c.id}.name`]}
                      />
                    ) : (
                      <b>{c.name}</b>
                    )}
                  </td>
                  <td>
                    {canEdit ? (
                      <div className="d-flex flex-wrap gap-1">
                        {TYPE_ORDER.map((t) => {
                          const isHidden = hidden.includes(t.key);
                          const tLabel = typeLabelFor(t, versionDB);
                          return (
                            <Button
                              key={t.key}
                              color={isHidden ? "secondary" : "light"}
                              outline={!isHidden}
                              size="sm"
                              onClick={() => handleToggleHidden(c.id, t.key, !isHidden)}
                              title={isHidden ? `Mostrar en ${tLabel}` : `Ocultar en ${tLabel}`}
                              style={{ fontSize: 11, padding: "2px 8px" }}
                            >
                              {isHidden && (
                                <span
                                  className="material-symbols-rounded align-middle me-1"
                                  style={{ fontSize: 12 }}
                                >
                                  visibility_off
                                </span>
                              )}
                              {tLabel}
                            </Button>
                          );
                        })}
                      </div>
                    ) : hidden.length === 0 ? (
                      <span className="text-muted small">—</span>
                    ) : (
                      hidden.map((t) => {
                        const info = TYPE_ORDER.find((x) => x.key === t);
                        return (
                          <Badge
                            key={t}
                            color="light"
                            className="me-1 text-dark"
                            style={{ border: "1px solid #ddd" }}
                          >
                            {info ? typeLabelFor(info, versionDB) : t}
                          </Badge>
                        );
                      })
                    )}
                  </td>
                  {canEdit && (
                    <td className="text-end">
                      <Button
                        color="link"
                        size="sm"
                        onClick={() => handleDelete(c.id)}
                        title="Eliminar categoría"
                        style={{ color: "#f46a6a" }}
                      >
                        <span
                          className="material-symbols-rounded"
                          style={{ fontSize: 18 }}
                        >
                          delete
                        </span>
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </CardBody>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────
// Tab: Tarifas (timelapses + config por tipo)
// ─────────────────────────────────────────────────────────

// Calcula la duración de una tarifa plana a partir de timeInit/timeEnd.
// Soporta cruce de medianoche (ej. 23:00 → 06:00 = 7h). Si ambos son iguales
// se interpreta como 24h (tarifa todo el día). Mismo algoritmo que tc_qml.
function calcPlanaTime(timeInit, timeEnd) {
  if (!timeInit || !timeEnd) return 0;
  const [hi, mi] = String(timeInit).split(":").map((x) => parseInt(x, 10));
  const [he, me] = String(timeEnd).split(":").map((x) => parseInt(x, 10));
  if ([hi, mi, he, me].some((n) => isNaN(n))) return 0;
  const minsI = hi * 60 + mi;
  const minsE = he * 60 + me;
  let t = (minsE - minsI + 1440) % 1440;
  if (t === 0) t = 1440;
  return t;
}

// Input compuesto días/horas/minutos — una sola unidad a la vez.
// Al editar una unidad, las otras se vuelven 0 (mismo criterio que la UI
// local de tc_qml: "Establece solo una unidad de tiempo").
function DurationInput({ value, onChange, invalid }) {
  const v = Math.max(0, parseInt(value, 10) || 0);
  const days = Math.floor(v / 1440);
  const hoursRemainder = Math.floor((v % 1440) / 60);
  const minutesRemainder = v % 60;

  // Detectar qué unidad representa mejor el valor actual.
  // - Si es múltiplo exacto de 1440 y > 0 → días
  // - Si es múltiplo exacto de 60 y > 0 → horas
  // - Si no → minutos
  let activeUnit = "minutes";
  if (v > 0 && v % 1440 === 0) activeUnit = "days";
  else if (v > 0 && v % 60 === 0) activeUnit = "hours";

  const displayDays = activeUnit === "days" ? v / 1440 : 0;
  const displayHours = activeUnit === "hours" ? v / 60 : 0;
  const displayMinutes = activeUnit === "minutes" ? v : 0;

  // Si el valor tiene mezcla (ej. 1504 = 1d+1h+4m, no múltiplo limpio),
  // mostramos desglosado pero grisado para que el usuario vea lo actual.
  const hasMixed = v > 0 && v !== displayDays * 1440 && v !== displayHours * 60 && v !== displayMinutes;

  const safe = (x, max) => {
    const n = parseInt(x, 10);
    if (isNaN(n) || n < 0) return 0;
    if (max != null && n > max) return max;
    return n;
  };

  const setUnit = (unit, raw) => {
    const n = safe(raw, unit === "hours" ? 9999 : unit === "minutes" ? 9999 : 9999);
    if (unit === "days") onChange(n * 1440);
    else if (unit === "hours") onChange(n * 60);
    else onChange(n);
  };

  // Para "mixed": mostramos los 3 valores descompuestos pero al editar
  // cualquiera, se resetea a una sola unidad.
  const dVal = hasMixed ? days : displayDays;
  const hVal = hasMixed ? hoursRemainder : displayHours;
  const mVal = hasMixed ? minutesRemainder : displayMinutes;

  const unitLabelStyle = {
    fontSize: 12,
    color: "#6c757d",
    lineHeight: "31px",
  };

  return (
    <div className="d-inline-flex align-items-center" style={{ gap: 6 }}>
      <Input
        type="number"
        bsSize="sm"
        min={0}
        value={dVal}
        invalid={!!invalid}
        onChange={(e) => setUnit("days", e.target.value)}
        style={{
          width: 60,
          opacity: activeUnit === "days" || hasMixed ? 1 : 0.5,
        }}
        title="Días (pone horas y minutos en 0)"
      />
      <span style={unitLabelStyle}>d</span>
      <Input
        type="number"
        bsSize="sm"
        min={0}
        value={hVal}
        invalid={!!invalid}
        onChange={(e) => setUnit("hours", e.target.value)}
        style={{
          width: 55,
          opacity: activeUnit === "hours" || hasMixed ? 1 : 0.5,
        }}
        title="Horas (pone días y minutos en 0)"
      />
      <span style={unitLabelStyle}>h</span>
      <Input
        type="number"
        bsSize="sm"
        min={0}
        value={mVal}
        invalid={!!invalid}
        onChange={(e) => setUnit("minutes", e.target.value)}
        style={{
          width: 60,
          opacity: activeUnit === "minutes" || hasMixed ? 1 : 0.5,
        }}
        title="Minutos (pone días y horas en 0)"
      />
      <span style={unitLabelStyle}>min</span>
    </div>
  );
}

// Editor de vigencia: Siempre o restringido por días / horarios.
// Estado derivado del timelapse: weekDay (array ints 0-6) + timeInit/timeEnd (HH:MM).
const WEEK_DAYS = [
  { idx: 0, short: "L" },
  { idx: 1, short: "Ma" },
  { idx: 2, short: "Mi" },
  { idx: 3, short: "J" },
  { idx: 4, short: "V" },
  { idx: 5, short: "S" },
  { idx: 6, short: "D" },
];
function VigenciaEditor({ tl, onPatch, errWeekDay }) {
  const hasRestriction =
    (Array.isArray(tl.weekDay) && tl.weekDay.length > 0) ||
    !!tl.timeInit ||
    !!tl.timeEnd;
  const [mode, setMode] = useState(hasRestriction ? "restringido" : "siempre");

  // Sincronizar modo si llegan cambios externos (reset, re-sync).
  useEffect(() => {
    setMode(hasRestriction ? "restringido" : "siempre");
  }, [hasRestriction]);

  const toggleDay = (dIdx) => {
    const current = Array.isArray(tl.weekDay) ? [...tl.weekDay] : [];
    const pos = current.indexOf(dIdx);
    if (pos >= 0) current.splice(pos, 1);
    else current.push(dIdx);
    current.sort((a, b) => a - b);
    onPatch({ weekDay: current });
  };

  const switchTo = (next) => {
    setMode(next);
    if (next === "siempre") {
      onPatch({ weekDay: [], timeInit: "", timeEnd: "" });
    } else {
      // Al activar restringido, si weekDay está vacío, poner L-V por defecto
      // (es lo más común — horario comercial); igual el usuario lo ajusta.
      const wd = Array.isArray(tl.weekDay) && tl.weekDay.length > 0
        ? tl.weekDay
        : [0, 1, 2, 3, 4];
      onPatch({ weekDay: wd });
    }
  };

  return (
    <div className="d-flex flex-wrap align-items-center" style={{ gap: 8, fontSize: 12 }}>
      <span className="text-muted">Vigencia:</span>
      <Button
        color={mode === "siempre" ? "primary" : "light"}
        outline={mode !== "siempre"}
        size="sm"
        style={{ padding: "2px 10px", fontSize: 12 }}
        onClick={() => switchTo("siempre")}
      >
        Siempre
      </Button>
      <Button
        color={mode === "restringido" ? "primary" : "light"}
        outline={mode !== "restringido"}
        size="sm"
        style={{ padding: "2px 10px", fontSize: 12 }}
        onClick={() => switchTo("restringido")}
      >
        Por día y horario
      </Button>
      {mode === "restringido" && (
        <>
          <div className="d-flex align-items-center" style={{ gap: 2, marginLeft: 6 }}>
            {WEEK_DAYS.map((d) => {
              const active = Array.isArray(tl.weekDay) && tl.weekDay.includes(d.idx);
              return (
                <Button
                  key={d.idx}
                  color={active ? "primary" : "light"}
                  outline={!active}
                  size="sm"
                  style={{ padding: "2px 8px", fontSize: 11, minWidth: 28 }}
                  onClick={() => toggleDay(d.idx)}
                  title={`Día ${d.short}`}
                >
                  {d.short}
                </Button>
              );
            })}
          </div>
          <Input
            type="time"
            bsSize="sm"
            value={tl.timeInit || ""}
            onChange={(e) => onPatch({ timeInit: e.target.value })}
            style={{ width: 110, display: "inline-block" }}
          />
          <span className="text-muted">→</span>
          <Input
            type="time"
            bsSize="sm"
            value={tl.timeEnd || ""}
            onChange={(e) => onPatch({ timeEnd: e.target.value })}
            style={{ width: 110, display: "inline-block" }}
          />
          {errWeekDay && (
            <span
              style={{
                color: "#f46a6a",
                fontSize: 11,
                marginLeft: 6,
              }}
            >
              {errWeekDay}
            </span>
          )}
        </>
      )}
    </div>
  );
}

// Detalle de un timelapse individual (fila dentro de la card del tipo)
function TimelapseRow({ tl, type, canEdit, updateDraft, errors = {} }) {
  const errLabel = errors[`timelapse.${tl.id}.label`];
  const errTime = errors[`timelapse.${tl.id}.time`];
  const errTimeInit = errors[`timelapse.${tl.id}.timeInit`];
  const errTimeEnd = errors[`timelapse.${tl.id}.timeEnd`];
  const errWeekDay = errors[`timelapse.${tl.id}.weekDay`];
  const parts = [];
  const duration = formatDuration(tl.time);
  if (duration) parts.push(duration);
  // Tolerancia no aplica a Abonados (cc)
  if (type !== "cc" && tl.tolerance > 0) parts.push(`tol. ${tl.tolerance} min`);
  if (tl.timeInit && tl.timeEnd) parts.push(`${tl.timeInit} – ${tl.timeEnd}`);
  const dayStr = formatWeekDay(tl.weekDay);
  if (dayStr) parts.push(dayStr);

  const isStartRate = type === "hour" && tl.onlyTime > 0;
  const isExclusive = type !== "hour" && tl.onlyTime > 0;

  // ¿Tiene configuración avanzada? (Vigencia o Tarifa de arranque activas)
  const hasVigencia =
    (Array.isArray(tl.weekDay) && tl.weekDay.length > 0) ||
    !!tl.timeInit ||
    !!tl.timeEnd;
  const hasArranque = (tl.onlyTime || 0) > 0;

  // Panel "Opciones avanzadas" colapsable (solo en modo edición, tipo hour).
  // Arranca expandido si ya hay algo configurado; si no, oculto con un link.
  const [advancedOpen, setAdvancedOpen] = useState(hasVigencia || hasArranque);
  // Si llega un cambio externo (reset, sync) que activa alguna config,
  // aseguramos que el panel se abra para no esconder valores configurados.
  useEffect(() => {
    if (hasVigencia || hasArranque) setAdvancedOpen(true);
  }, [hasVigencia, hasArranque]);

  // Helper para mutar este timelapse
  const patch = (mut) =>
    updateDraft((d) => {
      const t = d.timelapses.find((x) => x.id === tl.id);
      if (t) mut(t);
    });

  const handleDelete = () =>
    updateDraft((d) => {
      d.timelapses = d.timelapses.filter((x) => x.id !== tl.id);
      d.rates = d.rates.filter((r) => r.timelapseId !== tl.id);
    });

  if (canEdit) {
    const onPatch = (changes) => patch((t) => Object.assign(t, changes));
    // Bloque visualmente separado, con borde superior sutil entre filas.
    const rowStyle = { borderTop: "1px solid #eef2f7" };
    const cellStyle = { padding: "16px 12px", verticalAlign: "middle" };
    // Estilos compartidos para alineación vertical consistente
    const labelStyle = {
      fontSize: 12,
      color: "#6c757d",
      lineHeight: "31px", // matchea la altura del Input bsSize="sm"
      whiteSpace: "nowrap",
    };

    return (
      <tr style={rowStyle}>
        <td style={cellStyle}>
          {/* Fila principal: nombre + duración + tolerancia (wrap en pantallas chicas) */}
          <div
            className="d-flex flex-wrap align-items-center"
            style={{ columnGap: 28, rowGap: 8 }}
          >
            {/* Nombre — ancho fijo para que los siguientes grupos alineen */}
            <div style={{ width: 200, flexShrink: 0 }}>
              <Input
                type="text"
                bsSize="sm"
                value={tl.label || ""}
                invalid={!!errLabel}
                onChange={(e) => onPatch({ label: e.target.value })}
                placeholder="Nombre"
                style={{ fontWeight: 500 }}
              />
              <FieldError message={errLabel} />
            </div>

            {/* Duración (plana usa timeInit/timeEnd, cc es fijo) */}
            {type !== "cc" && type !== "plana" && (
              <div>
                <div className="d-inline-flex align-items-center" style={{ gap: 8 }}>
                  <span style={labelStyle}>Duración</span>
                  <DurationInput
                    value={tl.time || 0}
                    onChange={(v) => onPatch({ time: v })}
                    invalid={!!errTime}
                  />
                </div>
                <FieldError message={errTime} />
              </div>
            )}
            {type === "plana" && (
              <div>
                <div className="d-inline-flex align-items-center" style={{ gap: 8 }}>
                  <span style={labelStyle}>Horario</span>
                  <Input
                    type="time"
                    bsSize="sm"
                    value={tl.timeInit || ""}
                    invalid={!!errTimeInit}
                    onChange={(e) =>
                      onPatch({
                        timeInit: e.target.value,
                        time: calcPlanaTime(e.target.value, tl.timeEnd),
                      })
                    }
                    style={{ width: 110 }}
                    title="Inicio"
                  />
                  <span style={labelStyle}>→</span>
                  <Input
                    type="time"
                    bsSize="sm"
                    value={tl.timeEnd || ""}
                    invalid={!!errTimeEnd}
                    onChange={(e) =>
                      onPatch({
                        timeEnd: e.target.value,
                        time: calcPlanaTime(tl.timeInit, e.target.value),
                      })
                    }
                    style={{ width: 110 }}
                    title="Fin"
                  />
                  {/* Duración calculada — informativa */}
                  {tl.timeInit && tl.timeEnd && (
                    <span style={{ ...labelStyle, marginLeft: 4 }}>
                      ({formatDuration(calcPlanaTime(tl.timeInit, tl.timeEnd))})
                    </span>
                  )}
                </div>
                <FieldError message={errTimeInit || errTimeEnd} />
              </div>
            )}

            {/* Tolerancia — no aplica a Abonados (cc) */}
            {type !== "cc" && (
              <div className="d-inline-flex align-items-center" style={{ gap: 8 }}>
                <span style={labelStyle}>Tolerancia</span>
                <Input
                  type="number"
                  bsSize="sm"
                  min={0}
                  value={tl.tolerance || 0}
                  onChange={(e) =>
                    onPatch({ tolerance: parseInt(e.target.value, 10) || 0 })
                  }
                  style={{ width: 60 }}
                  title="Tolerancia en minutos"
                />
                <span style={labelStyle}>min</span>
              </div>
            )}
          </div>

          {/* Opciones avanzadas (solo tipo hour) — Vigencia + Tarifa de arranque.
              Colapsadas por defecto si no hay nada configurado, para no
              ocupar espacio visual en el caso más frecuente. */}
          {type === "hour" && (
            advancedOpen ? (
              <div style={{ marginTop: 10 }}>
                <VigenciaEditor tl={tl} onPatch={onPatch} errWeekDay={errWeekDay} />
                <div
                  className="d-inline-flex align-items-center"
                  style={{ fontSize: 12, gap: 8, marginTop: 8 }}
                >
                  <Input
                    type="checkbox"
                    checked={!!tl.onlyTime}
                    onChange={(e) =>
                      onPatch({
                        onlyTime: e.target.checked ? (tl.onlyTime || 1) : 0,
                      })
                    }
                    style={{ marginTop: 0 }}
                  />
                  <span className="text-muted">
                    Tarifa de arranque — primeras
                  </span>
                  <Input
                    type="number"
                    bsSize="sm"
                    min={1}
                    max={99}
                    value={tl.onlyTime || 1}
                    disabled={!tl.onlyTime}
                    onChange={(e) =>
                      onPatch({ onlyTime: parseInt(e.target.value, 10) || 1 })
                    }
                    style={{ width: 55, display: "inline-block" }}
                  />
                  <span className="text-muted">veces</span>
                </div>
                {/* Botón para cerrar el panel solo si no hay nada configurado */}
                {!hasVigencia && !hasArranque && (
                  <div style={{ marginTop: 6 }}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setAdvancedOpen(false);
                      }}
                      style={{ fontSize: 12, color: "#6c757d" }}
                    >
                      Ocultar opciones avanzadas
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginTop: 6 }}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setAdvancedOpen(true);
                  }}
                  style={{ fontSize: 12, color: "#556ee6" }}
                >
                  + Opciones avanzadas{" "}
                  <span className="text-muted">(vigencia, tarifa de arranque)</span>
                </a>
              </div>
            )
          )}
        </td>
        <td style={{ ...cellStyle, textAlign: "right" }}>
          <Button
            color="link"
            size="sm"
            onClick={handleDelete}
            title="Eliminar período"
            style={{ color: "#f46a6a", padding: 4 }}
          >
            <span className="material-symbols-rounded" style={{ fontSize: 18 }}>
              delete
            </span>
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        <div style={{ fontWeight: 600 }}>
          {!!tl.isBase && (
            <Badge
              color="primary"
              pill
              className="me-1"
              style={{ fontSize: 10 }}
              title="Tarifa base de fracción"
            >
              F
            </Badge>
          )}
          {tl.label}
          {!!isStartRate && (
            <Badge color="warning" pill className="ms-2" style={{ fontSize: 10 }}>
              arranque: primeras {tl.onlyTime}{" "}
              {tl.onlyTime === 1 ? "vez" : "veces"}
            </Badge>
          )}
          {!!isExclusive && (
            <Badge color="info" pill className="ms-2" style={{ fontSize: 10 }}>
              exclusivo
            </Badge>
          )}
        </div>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {parts.join(" · ")}
        </div>
      </td>
    </tr>
  );
}

// Card con los flags de config por tipo
// Componentes de edición de config (usados cuando canEdit=true)
function BoolField({ label, value, onChange, tooltip }) {
  return (
    <label
      className="d-flex align-items-center"
      style={{ fontSize: 13, minWidth: 180, cursor: "pointer" }}
      title={tooltip || ""}
    >
      <Input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked ? 1 : 0)}
        style={{ marginRight: 8, marginTop: 0 }}
      />
      <span>{label}</span>
    </label>
  );
}

function NumberField({ label, value, onChange, suffix, min = 0, max, width = 80 }) {
  const [local, setLocal] = useState(String(value ?? 0));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setLocal(String(value ?? 0));
  }, [value, focused]);
  const commit = () => {
    let n = parseInt(local, 10);
    if (isNaN(n)) n = 0;
    if (min != null && n < min) n = min;
    if (max != null && n > max) n = max;
    if (n !== value) onChange(n);
    setLocal(String(n));
  };
  return (
    <div style={{ fontSize: 13, minWidth: 180 }}>
      <div className="d-flex align-items-center">
        <span className="text-muted me-2">{label}:</span>
        <Input
          type="text"
          inputMode="numeric"
          bsSize="sm"
          value={local}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); commit(); }}
          onChange={(e) => setLocal(e.target.value)}
          style={{ width, display: "inline-block", fontVariantNumeric: "tabular-nums" }}
        />
        {suffix && <span className="text-muted ms-1">{suffix}</span>}
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, width = 100 }) {
  return (
    <div style={{ fontSize: 13, minWidth: 180 }}>
      <div className="d-flex align-items-center">
        <span className="text-muted me-2">{label}:</span>
        <Input
          type="text"
          bsSize="sm"
          value={value ?? ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          style={{ width, display: "inline-block" }}
        />
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div style={{ fontSize: 13, minWidth: 180 }}>
      <div className="d-flex align-items-center">
        <span className="text-muted me-2">{label}:</span>
        <Input
          type="select"
          bsSize="sm"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: "auto", display: "inline-block" }}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Input>
      </div>
    </div>
  );
}

function TypeConfigFlags({ type, config, canEdit, updateDraft, timelapsesOfType }) {
  if (!config) return null;
  const items = [];

  // setConf: helper para mutar config[key] en el draft.
  const setConf = (key, newValue) =>
    updateDraft && updateDraft((d) => {
      d.config[key] = newValue;
    });

  // Genera Flag/BoolField según canEdit
  const F = (key, label, tooltip) =>
    canEdit ? (
      <BoolField
        key={key}
        label={label}
        value={config[key]}
        tooltip={tooltip}
        onChange={(v) => setConf(key, v)}
      />
    ) : (
      <Flag key={key} active={!!config[key]} label={label} tooltip={tooltip} />
    );

  if (type === "hour") {
    // Layout especial para "Hora" — replica la UI local de tc_qml:
    //   Fila 1: [Fraccionar a partir de: ...]   [Tolerancia Inicial: N min]
    //   Fila 2: [✓ Permitir Reserva]   [✓ Permitir Cobro anticipado]

    // Opciones válidas para fraccionar: timelapses hour sin restricción de
    // días, ordenados ASC por duración, excluyendo el más pequeño.
    const hourBase = (timelapsesOfType || [])
      .filter(
        (t) =>
          !t.weekDay || !Array.isArray(t.weekDay) || t.weekDay.length === 0,
      )
      .sort((a, b) => (a.time || 0) - (b.time || 0));
    const fracOptions = [{ value: 0, label: "Sin fracción" }];
    hourBase.slice(1).forEach((t) => {
      fracOptions.push({
        value: t.time,
        label: `${t.label} (${formatDuration(t.time)})`,
      });
    });

    const setFracBase = (v) => {
      const newBase = parseInt(v, 10) || 0;
      updateDraft && updateDraft((d) => {
        d.config.timeBaseHour = newBase;
        // Actualizar también isBase en los timelapses hour — el marcado de
        // "base" lo maneja este selector, no un checkbox por fila.
        (d.timelapses || []).forEach((t) => {
          if (t.type !== "hour") return;
          const hasWeekDay = Array.isArray(t.weekDay) && t.weekDay.length > 0;
          t.isBase = !hasWeekDay && t.time === newBase && newBase > 0 ? 1 : 0;
        });
      });
    };

    return (
      <div className="mt-3 pt-3" style={{ borderTop: "1px solid #eef2f7" }}>
        <h6
          className="text-muted mb-3"
          style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.5px" }}
        >
          Configuración del bloque
        </h6>

        {/* Fila 1: Fraccionar a partir de + Tolerancia inicial */}
        <div
          className="d-flex flex-wrap align-items-center mb-2"
          style={{ columnGap: 28, rowGap: 10, fontSize: 13 }}
        >
          {canEdit ? (
            <SelectField
              label="Fraccionar a partir de"
              value={config.timeBaseHour || 0}
              onChange={setFracBase}
              options={fracOptions}
            />
          ) : (
            <Info
              label="Fraccionar a partir de"
              value={
                config.timeBaseHour > 0
                  ? formatDuration(config.timeBaseHour)
                  : "Sin fracción"
              }
            />
          )}
          {canEdit ? (
            <NumberField
              label="Tolerancia Inicial"
              value={config.initTolerance || 0}
              onChange={(v) => setConf("initTolerance", v)}
              suffix="min"
            />
          ) : (
            <Info
              label="Tolerancia Inicial"
              value={`${config.initTolerance || 0} min`}
            />
          )}
        </div>

        {/* Fila 2: Reserva + Cobro anticipado */}
        <div
          className="d-flex flex-wrap align-items-center"
          style={{ columnGap: 28, rowGap: 10, fontSize: 13 }}
        >
          {canEdit ? (
            <BoolField
              label="Permitir Reserva"
              value={config.hourReservation}
              onChange={(v) => setConf("hourReservation", v)}
            />
          ) : (
            <Flag active={!!config.hourReservation} label="Permitir Reserva" />
          )}
          {canEdit ? (
            <BoolField
              label="Permitir Cobro anticipado"
              value={config.hourPrepaidAllowed}
              onChange={(v) => setConf("hourPrepaidAllowed", v)}
            />
          ) : (
            <Flag
              active={!!config.hourPrepaidAllowed}
              label="Permitir Cobro anticipado"
            />
          )}
        </div>
      </div>
    );
  } else if (type === "tn") {
    items.push(F("tnReservation", "Permitir reserva"));
    items.push(F("tnPrepaidAllowed", "Permitir cobro anticipado"));
    items.push(F("tnOverflowLikeHour", "Calcular excedente como hora"));
  } else if (type === "stay") {
    // Layout especial para "Estadía" — replica la UI local de tc_qml:
    //   Fila 1: [✓ Horario de Finalización de Estadía] — cuando activo →
    //           [Horario: HH:MM] [✓ Editable]
    //   Fila 2: [✓ Calcular el Excedente como x Hora] [✓ Editar presencia manualmente]
    //   Fila 3: [✓ Permitir Reserva] [✓ Permitir Cobro anticipado]
    return (
      <div className="mt-3 pt-3" style={{ borderTop: "1px solid #eef2f7" }}>
        <h6
          className="text-muted mb-3"
          style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.5px" }}
        >
          Configuración del bloque
        </h6>

        {/* Fila 1: Horario de Finalización + input + Editable */}
        <div
          className="d-flex flex-wrap align-items-center mb-2"
          style={{ columnGap: 28, rowGap: 10, fontSize: 13 }}
        >
          {canEdit ? (
            <BoolField
              label="Horario de Finalización de Estadía"
              value={config.stayManualCut}
              onChange={(v) => setConf("stayManualCut", v)}
            />
          ) : (
            <Flag
              active={!!config.stayManualCut}
              label="Horario de Finalización de Estadía"
            />
          )}
          {config.stayManualCut ? (
            <>
              {canEdit ? (
                <div className="d-inline-flex align-items-center" style={{ gap: 8 }}>
                  <span
                    style={{
                      fontSize: 13,
                      color: "#495057",
                      lineHeight: "31px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Horario:
                  </span>
                  <Input
                    type="time"
                    bsSize="sm"
                    value={config.stayCut || ""}
                    onChange={(e) => setConf("stayCut", e.target.value)}
                    style={{ width: 110 }}
                  />
                </div>
              ) : (
                <Info label="Horario" value={config.stayCut || "—"} />
              )}
              {canEdit ? (
                <BoolField
                  label="Editable"
                  value={config.stayEditAllowed}
                  onChange={(v) => setConf("stayEditAllowed", v)}
                />
              ) : (
                <Flag active={!!config.stayEditAllowed} label="Editable" />
              )}
            </>
          ) : null}
        </div>

        {/* Fila 2: Excedente + Presencia manual */}
        <div
          className="d-flex flex-wrap align-items-center mb-2"
          style={{ columnGap: 28, rowGap: 10, fontSize: 13 }}
        >
          {canEdit ? (
            <BoolField
              label="Calcular el Excedente como x Hora"
              value={config.stayOverflowLikeHour}
              onChange={(v) => setConf("stayOverflowLikeHour", v)}
            />
          ) : (
            <Flag
              active={!!config.stayOverflowLikeHour}
              label="Calcular el Excedente como x Hora"
            />
          )}
          {canEdit ? (
            <BoolField
              label="Editar presencia manualmente"
              value={config.presenceStay}
              onChange={(v) => setConf("presenceStay", v)}
            />
          ) : (
            <Flag
              active={!!config.presenceStay}
              label="Editar presencia manualmente"
            />
          )}
        </div>

        {/* Fila 3: Reserva + Cobro anticipado */}
        <div
          className="d-flex flex-wrap align-items-center"
          style={{ columnGap: 28, rowGap: 10, fontSize: 13 }}
        >
          {canEdit ? (
            <BoolField
              label="Permitir Reserva"
              value={config.stayReservation}
              onChange={(v) => setConf("stayReservation", v)}
            />
          ) : (
            <Flag active={!!config.stayReservation} label="Permitir Reserva" />
          )}
          {canEdit ? (
            <BoolField
              label="Permitir Cobro anticipado"
              value={config.stayPrepaidAllowed}
              onChange={(v) => setConf("stayPrepaidAllowed", v)}
            />
          ) : (
            <Flag
              active={!!config.stayPrepaidAllowed}
              label="Permitir Cobro anticipado"
            />
          )}
        </div>
      </div>
    );
  } else if (type === "plana") {
    items.push(F("planaReservation", "Permitir reserva"));
    items.push(F("planaPrepaidAllowed", "Permitir cobro anticipado"));
    items.push(F(
      "planaApplyOnce",
      "Aplicar Tarifa Plana una sola vez por ticket",
      "Tras aplicar la primera franja plana, el resto del ticket se cobra por hora."
    ));
  } else if (type === "cc") {
    // Layout especial para Abonados — replica la UI local de tc_qml:
    //   Período de cobro: [Mensual] [Quincenal]
    //   ☐ Aplicar recargo por pago fuera de término
    //       (si activo → % + días debajo)
    //   ☐ Aplicar descuento por pago en término
    //       (si activo → % + días debajo)
    //   ☑ Editar presencia manualmente    ☐ Verificar placa al abrir ticket
    //   ☑ Mostrar notificación de deuda al ingreso
    //       A partir del día [N] de creado el cargo
    //       ( ) Sólo notificar   (•) Notificar y bloquear ingreso (requiere barrera)
    //       "Solo aplica al ingreso. El bloqueo solo tiene efecto si hay cámara ANPR con barrera."

    const period = parseInt(config.ccBillingPeriod || 30, 10);
    const periodLabel = period === 15 ? "Quincenal" : period === 30 ? "Mensual" : `${period} días`;
    const rowStyle = { columnGap: 28, rowGap: 10, fontSize: 13 };
    const subRowStyle = { columnGap: 28, rowGap: 10, fontSize: 13, marginLeft: 28 };

    return (
      <div className="mt-3 pt-3" style={{ borderTop: "1px solid #eef2f7" }}>
        <h6
          className="text-muted mb-3"
          style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.5px" }}
        >
          Configuración del bloque
        </h6>

        {/* Período de cobro */}
        <div className="d-flex flex-wrap align-items-center mb-2" style={rowStyle}>
          <span style={{ color: "#495057" }}>Período de cobro:</span>
          {canEdit ? (
            <div className="d-inline-flex" style={{ gap: 6 }}>
              <Button
                color={period === 30 ? "primary" : "light"}
                outline={period !== 30}
                size="sm"
                style={{ padding: "4px 16px", fontSize: 13 }}
                onClick={() => setConf("ccBillingPeriod", 30)}
              >
                Mensual
              </Button>
              <Button
                color={period === 15 ? "primary" : "light"}
                outline={period !== 15}
                size="sm"
                style={{ padding: "4px 16px", fontSize: 13 }}
                onClick={() => setConf("ccBillingPeriod", 15)}
              >
                Quincenal
              </Button>
            </div>
          ) : (
            <b>{periodLabel}</b>
          )}
        </div>

        {/* Aplicar recargo / descuento — mutuamente excluyentes.
            Al activar uno, limpiamos el otro (flag + sus campos en 0)
            para evitar estados inconsistentes en el cálculo de cargos. */}
        <div className="d-flex flex-wrap align-items-center mb-2" style={rowStyle}>
          {canEdit ? (
            <BoolField
              label="Aplicar recargo por pago fuera de término"
              value={config.ccSurcharge}
              onChange={(v) =>
                updateDraft && updateDraft((d) => {
                  d.config.ccSurcharge = v ? 1 : 0;
                  if (v) {
                    // Al activar recargo → limpiar descuento
                    d.config.ccDiscount = 0;
                    d.config.ccDiscountPercentage = 0;
                    d.config.ccDiscountDays = 0;
                  } else {
                    // Al desactivar recargo → limpiar sus campos
                    d.config.ccSurchargePercentage = 0;
                    d.config.ccSurchargeDays = 0;
                  }
                })
              }
            />
          ) : (
            <Flag
              active={!!config.ccSurcharge}
              label="Aplicar recargo por pago fuera de término"
            />
          )}
        </div>
        {!!config.ccSurcharge && (
          <div className="d-flex flex-wrap align-items-center mb-2" style={subRowStyle}>
            {canEdit ? (
              <>
                <NumberField
                  label="Recargo"
                  value={config.ccSurchargePercentage || 0}
                  onChange={(v) => setConf("ccSurchargePercentage", v)}
                  suffix="%"
                  max={100}
                />
                <NumberField
                  label="Días de atraso"
                  value={config.ccSurchargeDays || 0}
                  onChange={(v) => setConf("ccSurchargeDays", v)}
                  suffix="días"
                />
              </>
            ) : (
              <Info
                label="Recargo"
                value={`${config.ccSurchargePercentage || 0}% a los ${config.ccSurchargeDays || 0} días`}
              />
            )}
          </div>
        )}

        {/* Aplicar descuento */}
        <div className="d-flex flex-wrap align-items-center mb-2" style={rowStyle}>
          {canEdit ? (
            <BoolField
              label="Aplicar descuento por pago en término"
              value={config.ccDiscount}
              onChange={(v) =>
                updateDraft && updateDraft((d) => {
                  d.config.ccDiscount = v ? 1 : 0;
                  if (v) {
                    // Al activar descuento → limpiar recargo
                    d.config.ccSurcharge = 0;
                    d.config.ccSurchargePercentage = 0;
                    d.config.ccSurchargeDays = 0;
                  } else {
                    // Al desactivar descuento → limpiar sus campos
                    d.config.ccDiscountPercentage = 0;
                    d.config.ccDiscountDays = 0;
                  }
                })
              }
            />
          ) : (
            <Flag
              active={!!config.ccDiscount}
              label="Aplicar descuento por pago en término"
            />
          )}
        </div>
        {!!config.ccDiscount && (
          <div className="d-flex flex-wrap align-items-center mb-2" style={subRowStyle}>
            {canEdit ? (
              <>
                <NumberField
                  label="Descuento"
                  value={config.ccDiscountPercentage || 0}
                  onChange={(v) => setConf("ccDiscountPercentage", v)}
                  suffix="%"
                  max={100}
                />
                <NumberField
                  label="Días antes del vencimiento"
                  value={config.ccDiscountDays || 0}
                  onChange={(v) => setConf("ccDiscountDays", v)}
                  suffix="días"
                />
              </>
            ) : (
              <Info
                label="Descuento"
                value={`${config.ccDiscountPercentage || 0}% antes de ${config.ccDiscountDays || 0} días`}
              />
            )}
          </div>
        )}

        {/* Presencia + Verificar placa (misma fila) */}
        <div className="d-flex flex-wrap align-items-center mb-2" style={rowStyle}>
          {canEdit ? (
            <BoolField
              label="Editar presencia manualmente"
              value={config.presenceCc}
              onChange={(v) => setConf("presenceCc", v)}
            />
          ) : (
            <Flag
              active={!!config.presenceCc}
              label="Editar presencia manualmente"
            />
          )}
          {canEdit ? (
            <BoolField
              label="Verificar placa al abrir ticket"
              value={config.ccVerifyPlate}
              onChange={(v) => setConf("ccVerifyPlate", v)}
            />
          ) : (
            <Flag
              active={!!config.ccVerifyPlate}
              label="Verificar placa al abrir ticket"
            />
          )}
        </div>

        {/* Notificación de deuda */}
        <div className="d-flex flex-wrap align-items-center mb-2" style={rowStyle}>
          {canEdit ? (
            <BoolField
              label="Mostrar notificación de deuda al ingreso"
              value={config.ccDebtWarnEnabled}
              onChange={(v) => setConf("ccDebtWarnEnabled", v)}
            />
          ) : (
            <Flag
              active={!!config.ccDebtWarnEnabled}
              label="Mostrar notificación de deuda al ingreso"
            />
          )}
        </div>
        {!!config.ccDebtWarnEnabled && (
          <div style={{ marginLeft: 28 }}>
            {/* Sub-fila 1: A partir del día N */}
            <div className="d-flex flex-wrap align-items-center mb-2" style={rowStyle}>
              <div className="d-inline-flex align-items-center" style={{ gap: 8 }}>
                <span style={{ color: "#495057" }}>A partir del día</span>
                {canEdit ? (
                  <Input
                    type="number"
                    bsSize="sm"
                    min={1}
                    value={config.ccDebtWarnDays || 1}
                    onChange={(e) =>
                      setConf("ccDebtWarnDays", parseInt(e.target.value, 10) || 1)
                    }
                    style={{ width: 60 }}
                  />
                ) : (
                  <b>{config.ccDebtWarnDays || 1}</b>
                )}
                <span style={{ color: "#495057" }}>de creado el cargo</span>
              </div>
            </div>
            {/* Sub-fila 2: radio — Sólo notificar / Notificar y bloquear */}
            <div className="d-flex flex-wrap align-items-center mb-2" style={rowStyle}>
              {canEdit ? (
                <>
                  <label
                    className="d-inline-flex align-items-center"
                    style={{ gap: 6, cursor: "pointer", margin: 0 }}
                  >
                    <Input
                      type="radio"
                      name="ccDebtWarnMode"
                      // Tolerante a propósito: cualquier valor distinto de
                      // "warn_block" significa sólo notificar. Comparar contra
                      // "only_warn" dejaba los DOS radios vacíos cuando la
                      // máquina publicaba su literal interno ("warn").
                      checked={config.ccDebtWarnMode !== "warn_block"}
                      onChange={() => setConf("ccDebtWarnMode", "only_warn")}
                      style={{ marginTop: 0 }}
                    />
                    <span>Sólo notificar</span>
                  </label>
                  <label
                    className="d-inline-flex align-items-center"
                    style={{ gap: 6, cursor: "pointer", margin: 0 }}
                  >
                    <Input
                      type="radio"
                      name="ccDebtWarnMode"
                      checked={config.ccDebtWarnMode === "warn_block"}
                      onChange={() => setConf("ccDebtWarnMode", "warn_block")}
                      style={{ marginTop: 0 }}
                    />
                    <span>Notificar y bloquear ingreso</span>
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      (requiere barrera)
                    </span>
                  </label>
                </>
              ) : (
                <Info
                  label="Modo"
                  value={
                    config.ccDebtWarnMode === "warn_block"
                      ? "Notificar y bloquear ingreso"
                      : "Sólo notificar"
                  }
                />
              )}
            </div>
            {/* Texto de aclaración */}
            <div
              className="text-muted"
              style={{ fontSize: 12, fontStyle: "italic", marginTop: 2 }}
            >
              Solo aplica al ingreso. El bloqueo solo tiene efecto si hay cámara
              ANPR con barrera.
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3" style={{ borderTop: "1px solid #eef2f7" }}>
      <h6 className="text-muted mb-3" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        Configuración del bloque
      </h6>
      <div
        className="d-flex flex-wrap align-items-center"
        style={{ columnGap: 28, rowGap: 10, fontSize: 13 }}
      >
        {items}
      </div>
    </div>
  );
}

// Card por tipo en la pestaña Tarifas
function TypeCard({ type, typeLabel, icon, color, timelapses, config, visibleCatsCount, canEdit, updateDraft, genNegId, errors = {} }) {
  if (timelapses.length === 0 && !config && !canEdit) return null;

  const handleAddTimelapse = () => {
    if (!updateDraft || !genNegId) return;
    const newId = genNegId();
    // Defaults por tipo
    const defaults = {
      hour: { label: "Nuevo período", time: 60, tolerance: 0 },
      tn: { label: "Turno", time: 480, tolerance: 0 },
      stay: { label: "Estadía", time: 1440, tolerance: 0 },
      plana: { label: "Plana", time: 1, tolerance: 0, timeInit: "00:00", timeEnd: "06:00" },
      cc: { label: "Mensual", time: 43200, tolerance: 0 },
    };
    const def = defaults[type] || {};
    updateDraft((d) => {
      d.timelapses.push({
        id: newId,
        label: def.label,
        type,
        time: def.time,
        tolerance: def.tolerance,
        weekDay: [],
        timeInit: def.timeInit || "",
        timeEnd: def.timeEnd || "",
        onlyTime: 0,
        isBase: 0,
      });
    });
  };

  return (
    <Card className="mb-4 shadow-sm" style={{ borderTop: `3px solid ${color}` }}>
      <CardBody>
        <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
          <span
            className="material-symbols-rounded me-2"
            style={{ fontSize: 22, color }}
          >
            {icon}
          </span>
          <h5 className="mb-0">{typeLabel}</h5>
          <Badge color="light" className="ms-2 text-muted">
            {timelapses.length} período{timelapses.length !== 1 ? "s" : ""}
          </Badge>
          {visibleCatsCount > 0 && (
            <Badge color="light" className="ms-1 text-muted">
              {visibleCatsCount} categoría{visibleCatsCount !== 1 ? "s" : ""}
            </Badge>
          )}
          {canEdit && (
            <Button
              color="primary"
              outline
              size="sm"
              className="ms-auto"
              onClick={handleAddTimelapse}
            >
              <span
                className="material-symbols-rounded align-middle me-1"
                style={{ fontSize: 14 }}
              >
                add
              </span>
              Agregar período
            </Button>
          )}
        </div>

        {timelapses.length > 0 ? (
          <div className="table-responsive">
            <Table
              className={canEdit ? "mb-0 align-middle table-borderless" : "mb-0 align-middle"}
              size="sm"
            >
              <thead className="table-light">
                <tr>
                  <th>Período</th>
                  {canEdit && <th style={{ width: 80 }} />}
                </tr>
              </thead>
              <tbody>
                {timelapses.map((tl) => (
                  <TimelapseRow
                    key={tl.id}
                    tl={tl}
                    type={type}
                    canEdit={canEdit}
                    updateDraft={updateDraft}
                    errors={errors}
                  />
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="text-muted small">Sin períodos configurados.</div>
        )}

        <TypeConfigFlags
          type={type}
          config={config}
          canEdit={canEdit}
          updateDraft={updateDraft}
          timelapsesOfType={timelapses}
        />
      </CardBody>
    </Card>
  );
}

function TariffsTab({ timelapsesByType, config, categoriesByType, canEdit, updateDraft, genNegId, versionDB, errors = {} }) {
  // Solo mostrar bloques que tengan al menos un período configurado —
  // los tipos sin tarifas ensucian la vista. Los ausentes se pueden
  // crear desde el botón de agregar (solo en modo edit).
  const presentTypes = TYPE_ORDER.filter(
    (t) => (timelapsesByType[t.key] || []).length > 0
  );
  const missingTypes = TYPE_ORDER.filter(
    (t) => (timelapsesByType[t.key] || []).length === 0
  );

  // Defaults para crear el primer timelapse cuando el usuario agrega un tipo.
  const defaultsByType = {
    hour: { label: "Hora", time: 60, tolerance: 0 },
    tn: { label: "Turno", time: 480, tolerance: 0 },
    stay: { label: "Estadía", time: 1440, tolerance: 0 },
    plana: {
      label: "Tarifa Plana",
      time: 420,
      tolerance: 0,
      timeInit: "00:00",
      timeEnd: "07:00",
    },
    cc: { label: "Mensual", time: 43200, tolerance: 0 },
  };

  const handleAddType = (typeKey) => {
    if (!updateDraft || !genNegId) return;
    const def = defaultsByType[typeKey] || {};
    const newId = genNegId();
    updateDraft((d) => {
      d.timelapses.push({
        id: newId,
        label: def.label || "Nuevo período",
        type: typeKey,
        time: def.time || 0,
        tolerance: def.tolerance || 0,
        weekDay: [],
        timeInit: def.timeInit || "",
        timeEnd: def.timeEnd || "",
        onlyTime: 0,
        isBase: 0,
      });
    });
  };

  return (
    <div>
      {/* Bloques de tipos con tarifas configuradas */}
      {presentTypes.map((t) => (
        <TypeCard
          key={t.key}
          type={t.key}
          typeLabel={typeLabelFor(t, versionDB)}
          icon={t.icon}
          color={t.color}
          timelapses={timelapsesByType[t.key] || []}
          config={config}
          visibleCatsCount={categoriesByType[t.key] || 0}
          canEdit={canEdit}
          updateDraft={updateDraft}
          genNegId={genNegId}
          errors={errors}
        />
      ))}

      {/* Empty state en read-only cuando el cliente no tiene NINGUNA tarifa */}
      {!canEdit && presentTypes.length === 0 && (
        <Card>
          <CardBody className="text-muted text-center py-4">
            Este cliente aún no tiene tarifas configuradas.
          </CardBody>
        </Card>
      )}

      {/* Panel para agregar tipos ausentes (abajo de todo — discreto, solo
          si el usuario lo necesita). */}
      {canEdit && missingTypes.length > 0 && (
        <Card
          className="mt-3 mb-4"
          style={{
            borderStyle: "dashed",
            borderColor: "#ced4da",
            background: "transparent",
          }}
        >
          <CardBody className="py-3">
            <div
              className="d-flex flex-wrap align-items-center"
              style={{ columnGap: 12, rowGap: 8 }}
            >
              <span
                className="material-symbols-rounded"
                style={{ fontSize: 18, color: "#adb5bd" }}
              >
                add_circle
              </span>
              <span style={{ fontSize: 12, color: "#6c757d" }}>
                ¿Necesitás otro tipo de tarifa?
              </span>
              {missingTypes.map((t) => (
                <Button
                  key={t.key}
                  color="light"
                  outline
                  size="sm"
                  onClick={() => handleAddType(t.key)}
                  style={{ fontSize: 12, padding: "4px 12px" }}
                >
                  <span
                    className="material-symbols-rounded align-middle me-1"
                    style={{ fontSize: 14, color: t.color }}
                  >
                    {t.icon}
                  </span>
                  {typeLabelFor(t, versionDB)}
                </Button>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Tab: Precios (matriz)
// ─────────────────────────────────────────────────────────

// Detecta fracciones inconsistentes: cuando una tarifa por hora acumulada
// (prev.value * ratio) resulta MENOR al bloque superior. Mismo algoritmo
// que checkFractions() de tc_qml (backend/config_rates.py).
// Retorna Set de keys "catId_tlId" con problema.
function computeFractionErrors(hourTimelapses, visibleCats, rateMap) {
  // Solo timelapses sin weekDay (tarifas "base", no horarios especiales)
  const baseTls = hourTimelapses.filter(
    (tl) => !Array.isArray(tl.weekDay) || tl.weekDay.length === 0
  );
  const sorted = [...baseTls].sort((a, b) => (a.time || 0) - (b.time || 0));

  const errors = new Set();
  visibleCats.forEach((cat) => {
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const curr = sorted[i];
      const prevVal = parseFloat(rateMap[`${cat.id}_${prev.id}`] || 0);
      const currVal = parseFloat(rateMap[`${cat.id}_${curr.id}`] || 0);
      const prevTime = parseFloat(prev.time || 1);
      const currTime = parseFloat(curr.time || 1);

      if (prevVal > 0 && currVal > 0) {
        const ratio = currTime / prevTime;
        const accumulated = prevVal * ratio;
        if (accumulated < currVal) {
          errors.add(`${cat.id}_${curr.id}`);
        }
      }
    }
  });
  return errors;
}

// Input inline para editar un precio. Usa string local para no perder
// el foco ni el cursor al re-renderizar, y commitea al blur o Enter.
function RateInput({ value, onChange, isZero }) {
  const [local, setLocal] = useState(String(value ?? 0));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setLocal(String(value ?? 0));
  }, [value, focused]);

  const commit = () => {
    const n = parseFloat(local.replace(",", "."));
    const final = isNaN(n) || n < 0 ? 0 : n;
    if (final !== value) onChange(final);
    setLocal(String(final));
  };

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={local}
      bsSize="sm"
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        commit();
      }}
      onChange={(e) => setLocal(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        }
      }}
      className="text-end"
      style={{
        minWidth: 90,
        maxWidth: 120,
        display: "inline-block",
        fontVariantNumeric: "tabular-nums",
        color: isZero ? "#c0392b" : undefined,
        fontWeight: isZero ? 600 : 500,
      }}
    />
  );
}

function PricesTypeTable({
  type,
  typeLabel,
  icon,
  color,
  timelapses,
  categories,
  rateMap,
  baseTimelapseId,
  canEdit,
  updateDraft,
}) {
  const visibleCats = useMemo(
    () =>
      categories.filter(
        (c) => !Array.isArray(c.hiddenBy) || !c.hiddenBy.includes(type)
      ),
    [categories, type]
  );

  // Fracciones inconsistentes (solo tiene sentido en tipo hour)
  const fractionErrors = useMemo(
    () =>
      type === "hour"
        ? computeFractionErrors(timelapses, visibleCats, rateMap)
        : new Set(),
    [type, timelapses, visibleCats, rateMap]
  );

  // Hay algún $0 en esta tabla?
  const hasZero = useMemo(() => {
    for (const tl of timelapses) {
      for (const c of visibleCats) {
        const v = rateMap[`${c.id}_${tl.id}`];
        if (v === 0 || v == null) return true;
      }
    }
    return false;
  }, [timelapses, visibleCats, rateMap]);

  // Early return DESPUÉS de todos los hooks (regla de React)
  if (timelapses.length === 0) return null;

  return (
    <Card className="mb-4 shadow-sm" style={{ borderTop: `3px solid ${color}` }}>
      <CardBody>
        <div className="d-flex align-items-center mb-3 flex-wrap">
          <span
            className="material-symbols-rounded me-2"
            style={{ fontSize: 22, color }}
          >
            {icon}
          </span>
          <h5 className="mb-0">{typeLabel}</h5>
          <Badge color="light" className="ms-2 text-muted">
            {timelapses.length} período{timelapses.length !== 1 ? "s" : ""}
          </Badge>
          <div className="ms-auto d-flex gap-2">
            {fractionErrors.size > 0 && (
              <Badge
                color="warning"
                style={{ fontSize: 11, padding: "6px 10px" }}
              >
                <span
                  className="material-symbols-rounded align-middle me-1"
                  style={{ fontSize: 14 }}
                >
                  warning
                </span>
                Fracciones inconsistentes
              </Badge>
            )}
            {hasZero && (
              <Badge
                color="danger"
                style={{ fontSize: 11, padding: "6px 10px" }}
              >
                <span
                  className="material-symbols-rounded align-middle me-1"
                  style={{ fontSize: 14 }}
                >
                  error
                </span>
                Valores en $0
              </Badge>
            )}
          </div>
        </div>

        <div className="table-responsive">
          <Table className="mb-0 align-middle" bordered hover>
            <thead className="table-light">
              <tr>
                <th style={{ minWidth: 220 }}>Período</th>
                {visibleCats.map((c) => (
                  <th key={c.id} className="text-end">
                    {c.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timelapses.map((tl) => (
                <tr key={tl.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>
                      {baseTimelapseId === tl.id && (
                        <Badge
                          color="info"
                          pill
                          className="me-1"
                          title="Tarifa base de fracción"
                          style={{ fontSize: 10 }}
                        >
                          F
                        </Badge>
                      )}
                      {tl.label}
                    </div>
                    {/* Metadata (duración, tolerancia, horarios, días) — no aplica a Abonados */}
                    {type !== "cc" && (
                      <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                        {[
                          formatDuration(tl.time),
                          tl.tolerance > 0 ? `tol. ${tl.tolerance} min` : null,
                          tl.timeInit && tl.timeEnd
                            ? `${tl.timeInit}–${tl.timeEnd}`
                            : null,
                          formatWeekDay(tl.weekDay),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    )}
                  </td>
                  {visibleCats.map((c) => {
                    const value = rateMap[`${c.id}_${tl.id}`];
                    const isZero = value === 0 || value == null;
                    const isFractionError = fractionErrors.has(`${c.id}_${tl.id}`);
                    return (
                      <td
                        key={c.id}
                        className="text-end"
                        title={
                          isFractionError
                            ? "La acumulación de la fracción es menor al bloque superior"
                            : ""
                        }
                        style={{
                          fontVariantNumeric: "tabular-nums",
                          whiteSpace: "nowrap",
                          color: isZero && !canEdit ? "#c0392b" : undefined,
                          fontWeight: isZero || isFractionError ? 600 : 500,
                          backgroundColor: isFractionError
                            ? "rgba(241, 180, 76, 0.18)"
                            : undefined,
                        }}
                      >
                        {canEdit ? (
                          <RateInput
                            value={value || 0}
                            onChange={(newVal) =>
                              updateDraft((d) => {
                                const rate = d.rates.find(
                                  (r) =>
                                    r.categoryId === c.id &&
                                    r.timelapseId === tl.id
                                );
                                if (rate) {
                                  rate.value = newVal;
                                } else {
                                  d.rates.push({
                                    categoryId: c.id,
                                    timelapseId: tl.id,
                                    value: newVal,
                                  });
                                }
                              })
                            }
                            isZero={isZero}
                          />
                        ) : (
                          formatCurrency(value || 0)
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
}

function PricesTab({ timelapsesByType, categories, rates, config, canEdit, updateDraft, versionDB }) {
  const rateMap = useMemo(() => {
    const m = {};
    (rates || []).forEach((r) => {
      m[`${r.categoryId}_${r.timelapseId}`] = r.value;
    });
    return m;
  }, [rates]);

  // Identificar tarifa base de fracción (timelapse hour con time == timeBaseHour)
  const baseTimelapseId = useMemo(() => {
    const tb = parseInt(config?.timeBaseHour || 0, 10);
    if (tb <= 0) return null;
    const hourTls = timelapsesByType.hour || [];
    const match = hourTls.find(
      (tl) =>
        (tl.time || 0) === tb &&
        (!Array.isArray(tl.weekDay) || tl.weekDay.length === 0)
    );
    return match ? match.id : null;
  }, [config, timelapsesByType]);

  // Chequeo global: ¿hay fracciones inconsistentes en algún tipo hour?
  const hasGlobalFractionErrors = useMemo(() => {
    const hourTls = timelapsesByType.hour || [];
    if (hourTls.length < 2) return false;
    const visibleCats = categories.filter(
      (c) => !Array.isArray(c.hiddenBy) || !c.hiddenBy.includes("hour")
    );
    return computeFractionErrors(hourTls, visibleCats, rateMap).size > 0;
  }, [timelapsesByType, categories, rateMap]);

  return (
    <div>
      {hasGlobalFractionErrors && (
        <Card
          className="mb-3"
          style={{
            borderLeft: "4px solid #f1b44c",
            backgroundColor: "rgba(241, 180, 76, 0.08)",
          }}
        >
          <CardBody className="py-3">
            <div className="d-flex align-items-start">
              <span
                className="material-symbols-rounded me-2"
                style={{ fontSize: 22, color: "#f1b44c" }}
              >
                warning
              </span>
              <div>
                <div style={{ fontWeight: 600, color: "#b37900" }}>
                  Fracciones inconsistentes
                </div>
                <div className="text-muted small">
                  Hay tarifas por hora donde la acumulación de fracciones
                  resulta más barata que el bloque superior. Las celdas
                  afectadas están marcadas en amarillo.
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      )}
      {TYPE_ORDER.map((t) => (
        <PricesTypeTable
          key={t.key}
          type={t.key}
          typeLabel={typeLabelFor(t, versionDB)}
          icon={t.icon}
          color={t.color}
          timelapses={timelapsesByType[t.key] || []}
          categories={categories}
          rateMap={rateMap}
          baseTimelapseId={t.key === "hour" ? baseTimelapseId : null}
          canEdit={canEdit}
          updateDraft={updateDraft}
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Página principal
// ─────────────────────────────────────────────────────────
const TABS = [
  { id: "categorias", label: "Categorías", icon: "directions_car" },
  { id: "tarifas", label: "Tarifas", icon: "tune" },
  { id: "precios", label: "Precios", icon: "sell" },
];

const Tarifario = ({ currentCompany, tariffs, canEditTariffs, versionDB }) => {
  const [activeTab, setActiveTab] = useState("precios");
  const [draft, setDraft] = useState(null);
  const [sending, setSending] = useState(false);
  const [serverErrors, setServerErrors] = useState(null);
  const lastSyncedAtRef = useRef(null);
  // Snapshot "baseline" — qué valores tenía el draft cuando se sincronizó
  // por última vez con Firestore. Se usa para detectar si el usuario hizo
  // edits locales independientes del snapshot. Comparar draft vs NUEVO
  // snapshot da falsos positivos cuando el snapshot cambió desde la máquina.
  const lastSyncedDraftRef = useRef(null);
  // Contador negativo para IDs de ítems nuevos (no persistidos en DB)
  const nextNegIdRef = useRef(-1);
  document.title = "Tarifario | Monitor";

  const machineKey = currentCompany?.id;

  // Inicializar / sincronizar draft con tariffs del store.
  // Si NO hay cambios locales y el snapshot cambió → re-sincronizar.
  // Si hay cambios locales y el snapshot cambió → avisamos con toast.
  useEffect(() => {
    if (!tariffs) {
      setDraft(null);
      lastSyncedAtRef.current = null;
      lastSyncedDraftRef.current = null;
      return;
    }
    if (!canEditTariffs) {
      // En modo read-only no usamos draft — renderizamos tariffs directo.
      setDraft(null);
      lastSyncedAtRef.current = tariffs.updatedAt;
      lastSyncedDraftRef.current = null;
      return;
    }
    // Recuperar draft persistido si el machineKey coincide
    if (!draft && machineKey) {
      try {
        const saved = localStorage.getItem(DRAFT_STORAGE_KEY(machineKey));
        if (saved) {
          const parsed = JSON.parse(saved);
          // El draft se guarda junto al updatedAt del snapshot base.
          // Si ese updatedAt difiere del actual, el snapshot cambió desde
          // que se hizo el draft — lo descartamos para evitar "isDirty"
          // espurio contra datos nuevos.
          const sameBase =
            parsed?.baseUpdatedAt === tariffs.updatedAt &&
            parsed?.draft?.schemaVersion;
          if (sameBase) {
            // Normalizar también el draft persistido: un valor heredado del
            // snapshot sobrevive a los reloads, así que "refrescá la página"
            // no alcanzaría como workaround.
            setDraft({
              ...parsed.draft,
              config: normalizeConfig(parsed.draft.config),
            });
            lastSyncedAtRef.current = tariffs.updatedAt;
            lastSyncedDraftRef.current = buildInitialDraft(tariffs);
            return;
          }
          // Draft obsoleto → limpiar
          localStorage.removeItem(DRAFT_STORAGE_KEY(machineKey));
        }
      } catch (_) {}
      const initial = buildInitialDraft(tariffs);
      setDraft(initial);
      lastSyncedAtRef.current = tariffs.updatedAt;
      lastSyncedDraftRef.current = initial;
      return;
    }
    // Si llegó una actualización en vivo
    if (draft && tariffs.updatedAt !== lastSyncedAtRef.current) {
      const newSnapshot = buildInitialDraft(tariffs);
      // "Dirty" se mide contra el baseline (último snapshot sincronizado),
      // no contra el snapshot nuevo. Si el usuario no editó nada, draft
      // coincide con el baseline aunque Firestore traiga datos distintos
      // desde la máquina.
      const hadLocalEdits = lastSyncedDraftRef.current
        ? !payloadsEqual(draft, lastSyncedDraftRef.current)
        : false;
      // Si lo que llegó es justo lo que hay en pantalla, es el propio cambio
      // que se acaba de enviar, ya aplicado por la máquina: no queda nada sin
      // guardar. Antes caía en el aviso de abajo y, junto al «Tarifario
      // aplicado en la máquina», decía «Tenés cambios locales sin guardar».
      const llegoLoQueHayEnPantalla = payloadsEqual(draft, newSnapshot);
      if (!hadLocalEdits || llegoLoQueHayEnPantalla) {
        setDraft(newSnapshot);
      } else {
        toast.info(
          "La máquina actualizó su tarifario. Tenés cambios locales sin guardar — revisá antes de enviar.",
          { autoClose: 6000 }
        );
      }
      lastSyncedAtRef.current = tariffs.updatedAt;
      lastSyncedDraftRef.current = newSnapshot;
    }
  }, [tariffs, canEditTariffs, machineKey, draft]);

  // Persistir draft en localStorage cuando cambia.
  // Formato: { draft, baseUpdatedAt } — el baseUpdatedAt sirve para descartar
  // drafts obsoletos si luego el snapshot de Firestore cambia.
  useEffect(() => {
    if (!machineKey) return;
    if (!canEditTariffs || !draft || !tariffs) return;
    const snapshotDraft = buildInitialDraft(tariffs);
    if (payloadsEqual(draft, snapshotDraft)) {
      localStorage.removeItem(DRAFT_STORAGE_KEY(machineKey));
    } else {
      try {
        localStorage.setItem(
          DRAFT_STORAGE_KEY(machineKey),
          JSON.stringify({ draft, baseUpdatedAt: tariffs.updatedAt })
        );
      } catch (_) {}
    }
  }, [draft, tariffs, canEditTariffs, machineKey]);

  // ¿Draft != snapshot?
  const isDirty = useMemo(() => {
    if (!draft || !tariffs) return false;
    return !payloadsEqual(draft, buildInitialDraft(tariffs));
  }, [draft, tariffs]);

  // Mutators del draft (funciones que actualizan el estado local).
  // Todas reciben una función/valor y producen un nuevo draft inmutable.
  const updateDraft = (mutator) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = deepClone(prev);
      mutator(next);
      return next;
    });
    setServerErrors(null);
  };

  const genNegId = () => {
    const id = nextNegIdRef.current;
    nextNegIdRef.current -= 1;
    return id;
  };

  // Envío HTTP al TCSocket server
  const handleSend = async () => {
    if (!machineKey || !draft || sending) return;
    setSending(true);
    setServerErrors(null);
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("Sesión expirada. Volvé a iniciar sesión.");
        setSending(false);
        return;
      }
      const token = await user.getIdToken();
      // Del config mandamos solo lo que el usuario tocó (ver buildConfigDiff).
      // El resto del payload sí va completo: categorías, períodos y precios se
      // aplican por reconciliación, no por diff.
      // La baseline va normalizada igual que el draft: si se comparara contra
      // el snapshot crudo, un valor que la app publica en su formato interno
      // ("warn") se vería como "cambiado" y lo pisaríamos en cada guardado.
      const payload = {
        ...draft,
        config: buildConfigDiff(draft.config, normalizeConfig(tariffs?.config)),
      };
      const res = await fetch(`${TCSOCKET_URL}/monitor/tariffs-update`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key: machineKey, payload }),
      });
      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (_) {
        data = { message: text };
      }
      console.log("[tarifario] HTTP", res.status, data);
      if (!res.ok) {
        // Log técnico en consola, mensaje amigable para el usuario
        const code = data.error || `HTTP ${res.status}`;
        console.warn(`[tarifario] error code=${code}`, data);
        const friendly = friendlyErrorTitle(code, res.status);
        toast.error(friendly, { autoClose: 8000 });
        if (Array.isArray(data.errors)) {
          setServerErrors(data.errors);
        } else {
          // Si no hay lista de errores pero sí un message del backend, mostrarlo
          // debajo del toast como info adicional (más útil que nada).
          if (data.message && !["MACHINE_OFFLINE", "TIMEOUT", "AUTH_FAILED", "NO_PERMISSION"].includes(code)) {
            setServerErrors([{ field: "", message: data.message, code }]);
          } else {
            setServerErrors(null);
          }
        }
      } else {
        toast.success("Tarifario aplicado en la máquina");
        localStorage.removeItem(DRAFT_STORAGE_KEY(machineKey));
        // El draft se re-sincronizará con el próximo snapshot de Firestore.
      }
    } catch (err) {
      console.error("[tarifario] fetch error:", err);
      toast.error(`Error de red: ${err.message || err}`, { autoClose: 8000 });
    } finally {
      setSending(false);
    }
  };

  const handleDiscard = () => {
    if (!tariffs) return;
    setDraft(buildInitialDraft(tariffs));
    setServerErrors(null);
    if (machineKey) localStorage.removeItem(DRAFT_STORAGE_KEY(machineKey));
    toast.info("Cambios descartados");
  };

  // Fuente efectiva de datos para renderizar:
  // - Si editable y hay draft → usar draft (así los inputs reflejan ediciones pendientes).
  // - Si no → usar tariffs directo (read-only como TCADMIN).
  const effective = canEditTariffs && draft ? draft : tariffs;

  // Destructurar con defaults — seguro aunque tariffs sea null
  const categories = effective?.categories || [];
  const timelapses = effective?.timelapses || [];
  const rates = effective?.rates || [];
  const config = effective?.config || {};
  const updatedAt = tariffs?.updatedAt;

  // Agrupar timelapses por type — orden ASCENDENTE por tiempo
  // (bloques más chicos primero, como en la UI de tc_qml)
  const timelapsesByType = useMemo(() => {
    const by = {};
    timelapses.forEach((tl) => {
      const t = tl.type || "hour";
      (by[t] = by[t] || []).push(tl);
    });
    Object.keys(by).forEach((k) => {
      by[k].sort((a, b) => (a.time || 0) - (b.time || 0));
    });
    return by;
  }, [timelapses]);

  // Cantidad de categorías visibles por tipo (para mostrar en el header del Card)
  const categoriesByType = useMemo(() => {
    const result = {};
    TYPE_ORDER.forEach(({ key }) => {
      result[key] = categories.filter(
        (c) => !Array.isArray(c.hiddenBy) || !c.hiddenBy.includes(key)
      ).length;
    });
    return result;
  }, [categories]);

  // Detectar fracciones inconsistentes a nivel global (bloquea envío).
  // Usa los datos efectivos (draft si editable, sino tariffs).
  const hasFractionErrors = useMemo(() => {
    const hourTls = timelapsesByType.hour || [];
    if (hourTls.length < 2) return false;
    const visibleCats = categories.filter(
      (c) => !Array.isArray(c.hiddenBy) || !c.hiddenBy.includes("hour")
    );
    const rateMap = {};
    (rates || []).forEach((r) => {
      rateMap[`${r.categoryId}_${r.timelapseId}`] = r.value;
    });
    return computeFractionErrors(hourTls, visibleCats, rateMap).size > 0;
  }, [timelapsesByType, categories, rates]);

  // Errores client-side detectados en vivo en el draft.
  // Solo validamos en modo editable: si es read-only, no hay nada que validar.
  const clientErrors = useMemo(
    () => (canEditTariffs && draft ? validateDraftClient(draft) : {}),
    [canEditTariffs, draft]
  );
  const clientErrorCount = Object.keys(clientErrors).length;
  const hasClientErrors = clientErrorCount > 0;

  const updatedMoment = updatedAt ? moment(updatedAt) : null;

  // Early returns — DESPUÉS de todos los hooks
  if (!currentCompany?.id) {
    return (
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody className="text-center py-5">
              <span
                className="material-symbols-rounded d-block mb-2"
                style={{ fontSize: 48, color: "#ccc" }}
              >
                group
              </span>
              <h5 className="text-muted mb-0">
                Selecciona un cliente primero
              </h5>
              <p className="text-muted small mt-1">
                Ingresá a <b>Cuentas</b> y elegí un cliente para ver su tarifario.
              </p>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  if (!tariffs) {
    return (
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody className="text-center py-5">
              <span
                className="material-symbols-rounded d-block mb-2"
                style={{ fontSize: 48, color: "#ccc" }}
              >
                sync_disabled
              </span>
              <h5 className="text-muted mb-0">
                Este cliente aún no sincronizó su tarifario
              </h5>
              <p className="text-muted small mt-2">
                El tarifario se sube automáticamente desde tc_qml al editar una
                tarifa, precio, período o al hacer resync del Monitor.
                <br />
                Si el cliente está en la versión anterior (ticketcontrol2), el
                tarifario no se envía.
              </p>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Header */}
        <Row className="mb-3 align-items-center">
          <Col>
            <h4 className="mb-0">Tarifario</h4>
            <p className="text-muted small mb-0">
              {canEditTariffs
                ? "Configuración de categorías, tarifas y precios. Podés modificar y enviar los cambios a tu estacionamiento."
                : "Configuración de categorías, tarifas y precios del cliente. Solo lectura."}
            </p>
          </Col>
          <Col xs="auto">
            {updatedMoment && (
              <div className="text-end small text-muted">
                <span
                  className="material-symbols-rounded align-middle me-1"
                  style={{ fontSize: 16 }}
                >
                  update
                </span>
                Última sincronización:{" "}
                <b>{updatedMoment.locale("es").fromNow()}</b>
                <div style={{ fontSize: 11 }}>
                  {updatedMoment.format("DD/MM/YYYY HH:mm")}
                </div>
              </div>
            )}
          </Col>
        </Row>

        {/* Banner de funcionalidad experimental / en pruebas */}
        <Card
          className="mb-3"
          style={{
            borderLeft: "4px solid #50a5f1",
            backgroundColor: "rgba(80, 165, 241, 0.06)",
          }}
        >
          <CardBody className="py-2 px-3">
            <div className="d-flex align-items-start">
              <span
                className="material-symbols-rounded me-2"
                style={{ fontSize: 22, color: "#50a5f1", marginTop: 2 }}
              >
                science
              </span>
              <div style={{ flex: 1, fontSize: 13 }}>
                <div style={{ fontWeight: 600, color: "#2b6cb0" }}>
                  Funcionalidad en etapa de pruebas
                </div>
                <div className="text-muted" style={{ marginTop: 2 }}>
                  Esta sección está en período de evaluación. Ante cualquier
                  comportamiento inesperado, te pedimos que nos lo informes a
                  soporte técnico para poder mejorarla. Siempre podés ajustar
                  tarifas y precios desde TicketControl en el propio
                  estacionamiento.
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Tabs */}
        <Nav tabs className="mb-3">
          {TABS.map((tab) => (
            <NavItem key={tab.id}>
              <NavLink
                className={activeTab === tab.id ? "active" : ""}
                onClick={() => setActiveTab(tab.id)}
                style={{ cursor: "pointer" }}
              >
                <span
                  className="material-symbols-rounded me-1"
                  style={{ fontSize: 16, verticalAlign: "middle" }}
                >
                  {tab.icon}
                </span>
                {tab.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="categorias">
            {activeTab === "categorias" && (
              <CategoriesTab
                categories={categories}
                canEdit={canEditTariffs}
                updateDraft={updateDraft}
                genNegId={genNegId}
                versionDB={versionDB}
                errors={clientErrors}
              />
            )}
          </TabPane>
          <TabPane tabId="tarifas">
            {activeTab === "tarifas" && (
              <TariffsTab
                timelapsesByType={timelapsesByType}
                config={config}
                categoriesByType={categoriesByType}
                canEdit={canEditTariffs}
                updateDraft={updateDraft}
                genNegId={genNegId}
                versionDB={versionDB}
                errors={clientErrors}
              />
            )}
          </TabPane>
          <TabPane tabId="precios">
            {activeTab === "precios" && (
              <PricesTab
                timelapsesByType={timelapsesByType}
                categories={categories}
                rates={rates}
                config={config}
                canEdit={canEditTariffs}
                updateDraft={updateDraft}
                versionDB={versionDB}
              />
            )}
          </TabPane>
        </TabContent>

        {/* Barra de acción flotante (invertida — fondo oscuro para llamar la atención) */}
        {canEditTariffs && isDirty && (
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              background: "#2a3042",
              color: "#fff",
              borderTop: "1px solid #1b1e2c",
              boxShadow: "0 -4px 16px rgba(0,0,0,0.25)",
              padding: "14px 24px",
              zIndex: 1030,
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <span
              className="material-symbols-rounded"
              style={{
                fontSize: 24,
                color: hasClientErrors || hasFractionErrors
                  ? "#ff9a9a"
                  : "#ffd76b",
              }}
            >
              {hasClientErrors || hasFractionErrors ? "error" : "edit_note"}
            </span>
            <div style={{ flex: 1 }}>
              {hasClientErrors ? (
                <>
                  <div style={{ fontWeight: 600, color: "#ffd2d2", fontSize: 14 }}>
                    {clientErrorCount === 1
                      ? "1 campo con error"
                      : `${clientErrorCount} campos con errores`}
                  </div>
                  <div style={{ fontSize: 12, color: "#a8b0c3" }}>
                    Corregí los campos marcados en rojo antes de enviar.
                  </div>
                </>
              ) : hasFractionErrors ? (
                <>
                  <div style={{ fontWeight: 600, color: "#ffd2d2", fontSize: 14 }}>
                    No se puede guardar con fracciones inconsistentes
                  </div>
                  <div style={{ fontSize: 12, color: "#a8b0c3" }}>
                    Corregí las celdas marcadas en amarillo en la pestaña
                    Precios antes de enviar.
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontWeight: 600, color: "#fff", fontSize: 14 }}>
                    Tenés cambios sin guardar
                  </div>
                  <div style={{ fontSize: 12, color: "#a8b0c3" }}>
                    Los cambios se aplicarán en la máquina del estacionamiento
                    de forma inmediata al enviar. Asegurate de que tenga
                    conexión estable a internet.
                  </div>
                </>
              )}
            </div>
            <Button
              color="link"
              onClick={handleDiscard}
              disabled={sending}
              style={{
                textDecoration: "none",
                color: "#ced4e0",
                fontWeight: 500,
              }}
            >
              Descartar
            </Button>
            <Button
              color="success"
              onClick={handleSend}
              disabled={sending || hasFractionErrors || hasClientErrors}
              style={{ fontWeight: 600, padding: "8px 20px" }}
              title={
                hasClientErrors
                  ? "Corregí los campos con errores antes de enviar"
                  : hasFractionErrors
                  ? "Corregí las fracciones inconsistentes antes de enviar"
                  : ""
              }
            >
              {sending ? (
                <>
                  <Spinner size="sm" className="me-1" /> Enviando...
                </>
              ) : (
                <>
                  <span
                    className="material-symbols-rounded align-middle me-1"
                    style={{ fontSize: 16 }}
                  >
                    send
                  </span>
                  Enviar cambios
                </>
              )}
            </Button>
          </div>
        )}

        {/* Errores detallados debajo de la barra */}
        {canEditTariffs && serverErrors && serverErrors.length > 0 && (
          <Card
            className="mb-4"
            style={{
              borderLeft: "4px solid #f46a6a",
              backgroundColor: "rgba(244, 106, 106, 0.06)",
              marginBottom: 90,
            }}
          >
            <CardBody>
              <div className="d-flex align-items-start">
                <span
                  className="material-symbols-rounded me-2"
                  style={{ fontSize: 22, color: "#f46a6a" }}
                >
                  error
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: "#b84040" }}>
                    Problemas que impiden guardar
                  </div>
                  <ul className="small mb-0 mt-2" style={{ paddingLeft: 18 }}>
                    {serverErrors.map((e, i) => {
                      const humanField = humanizeField(e.field, draft);
                      return (
                        <li key={i} title={e.code ? `Código: ${e.code}` : ""}>
                          {humanField && (
                            <>
                              <b>{humanField}</b>: {" "}
                            </>
                          )}
                          {e.message}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Spacer para que el contenido no quede oculto por la barra flotante */}
        {canEditTariffs && isDirty && <div style={{ height: 80 }} />}
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentCompany: state.companies.currentCompany,
  tariffs: state.data.tariffs,
  canEditTariffs: state.data.canEditTariffs,
  versionDB: state.data.versionDB,
});

export default connect(mapStateToProps)(Tarifario);
